import React, { useState, useEffect } from 'react';
import AgencyOnboardingPortal from '../onboarding/AgencyOnboardingPortal';
import BatchUploadConsole from '../ingestion/BatchUploadConsole';

const DEFAULT_BATCHES = [
  {
    batchId: 'BATCH-ICICI-001',
    fileName: 'ICICI_Auto_Vintage_Sept2026.xlsx',
    bankName: 'ICICI Bank',
    productType: 'AUTO_LOAN',
    uploadDate: '2026-09-11 09:30 AM',
    totalCases: 2,
    status: 'Active (30-Day Cycle)',
    cases: [
      {
        id: 'CF22247723',
        customerName: 'Kumbham Dipinti Reddy',
        amount: '₹19,55,479.36',
        rawAmount: 1955479.36,
        phone: '+91 99492 92000',
        address: 'Dilsukhanagar, Hyderabad',
        status: 'Escalated to Field',
        attempts: 3,
        lastFeedback: '[Field PTP]: Promised ₹50,000 by 2026-09-11 14:00. Notes: Client agreed to clear via UPI.',
        escalatedToField: true,
        bucket: 'Vintage',
        collateral: '🚗 TS11FA3663 - Mahindra XUV 700',
        productType: 'AUTO_LOAN',
        bankName: 'ICICI Bank',
        assignedAgent: 'Suresh Kumar (Field Executive)',
        ptpDate: '2026-09-11',
        ptpAmount: 50000,
        channel: 'Field Agent'
      },
      {
        id: 'CF22258790',
        customerName: 'Hariprasad Garalapati',
        amount: '₹17,54,497.49',
        rawAmount: 1754497.49,
        phone: '+91 70322 11288',
        address: 'Kothapet, Hyderabad',
        status: 'Active Telecalling',
        attempts: 4,
        lastFeedback: '[PTP Logged]: Due by 2026-09-11 16:30 - Customer committed to transfer ₹35,000 via Netbanking.',
        escalatedToField: false,
        bucket: 'Vintage',
        collateral: '🚗 TS07KA6008 - TATA HARRIER',
        productType: 'AUTO_LOAN',
        bankName: 'ICICI Bank',
        assignedAgent: 'Priya Sharma (Senior Telecaller)',
        ptpDate: '2026-09-11',
        ptpAmount: 35000,
        channel: 'Telecaller'
      }
    ]
  },
  {
    batchId: 'BATCH-KOTAK-002',
    fileName: 'Kotak_CC_12thCycle_Sept.xlsx',
    bankName: 'Kotak Mahindra Bank',
    productType: 'CREDIT_CARD',
    uploadDate: '2026-09-10 02:15 PM',
    totalCases: 1,
    status: 'Active (Cycle-Based)',
    cases: [
      {
        id: 'CF22189342',
        customerName: 'Sampangi Sathya',
        amount: '₹24,720.00',
        rawAmount: 24720.00,
        phone: '+91 98888 44399',
        address: 'Jawahar Nagar, Hyderabad',
        status: 'Active Telecalling',
        attempts: 2,
        lastFeedback: '[PTP Logged]: Due by 2026-09-12 11:00 - Salary credited tomorrow.',
        escalatedToField: false,
        bucket: 'Flow',
        collateral: 'Credit Card Outstanding',
        productType: 'CREDIT_CARD',
        bankName: 'Kotak Mahindra Bank',
        assignedAgent: 'Priya Sharma (Senior Telecaller)',
        ptpDate: '2026-09-12',
        ptpAmount: 24720,
        channel: 'Telecaller'
      }
    ]
  }
];

const ManagerPortalLayout = () => {
  // Screen Width Detection for Mobile Viewport Support
  const [isMobileScreen, setIsMobileScreen] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => setIsMobileScreen(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Session State
  const [currentUser, setCurrentUser] = useState(() => {
    const savedRole = localStorage.getItem('debtone_active_role');
    if (savedRole) {
      return {
        isAuthenticated: true,
        role: savedRole,
        name: savedRole === 'SUPER_ADMIN' ? 'Platform Administrator' : 
              savedRole === 'AGENCY_MANAGER' ? 'Manoj Kumar (Operations Head)' : 
              savedRole === 'TELECALLER' ? 'Priya Sharma (Senior Telecaller)' : 'Suresh Kumar (Field Executive)',
        agencyName: 'Apex Recovery Solutions',
        assignedBank: 'Multi-Bank'
      };
    }
    return {
      isAuthenticated: true,
      role: 'AGENCY_MANAGER',
      name: 'Manoj Kumar (Operations Head)',
      agencyName: 'Apex Recovery Solutions',
      assignedBank: 'Multi-Bank'
    };
  });

  const [loginEmail, setLoginEmail] = useState('');
  const [loginRole, setLoginRole] = useState('AGENCY_MANAGER');
  const [managerTab, setManagerTab] = useState('Operations Overview');
  
  // Workspace Filters & Drill-Down State
  const [selectedBankFilter, setSelectedBankFilter] = useState('ALL');
  const [selectedBatchId, setSelectedBatchId] = useState(null); // Drill-down into specific uploaded file
  const [selectedStageFilter, setSelectedStageFilter] = useState('ALL');
  const [showArchivedBatches, setShowArchivedBatches] = useState(false);

  // Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 25;

  // Persistent Batch & Case State
  const [uploadedBatches, setUploadedBatches] = useState(() => {
    try {
      const savedBatches = localStorage.getItem('debtone_uploaded_batches_v2');
      if (savedBatches) {
        const parsed = JSON.parse(savedBatches);
        if (Array.isArray(parsed) && parsed.length > 0) return parsed;
      }
    } catch (e) {}
    return DEFAULT_BATCHES;
  });

  // Flatten all cases across all active batches for master overview
  const allMasterCases = uploadedBatches.flatMap(b => b.cases || []);

  const [activeCaseIndex, setActiveCaseIndex] = useState(0);
  const [trackingAgent, setTrackingAgent] = useState(null);

  // View Modes
  const [telecallerMode, setTelecallerMode] = useState('TABLE');
  const [fieldMode, setFieldMode] = useState('GUIDED');

  // Manager Allocation Rules State
  const [allocationRule, setAllocationRule] = useState({
    highValueThreshold: '500000',
    seniorRouting: true,
    distributionMode: 'ROUND_ROBIN'
  });
  const [ruleSavedMsg, setRuleSavedMsg] = useState('');

  // DRA & Team State with Leave Status & Telemetry
  const [draHolders] = useState([
    { id: 'DRA-ICICI-99201', name: 'Ramesh Verma (Certified)', bank: 'ICICI Bank' },
    { id: 'DRA-KOTAK-44102', name: 'Pooja Hegde (Certified)', bank: 'Kotak Mahindra Bank' }
  ]);

  const [proxyAssignments, setProxyAssignments] = useState([
    {
      id: 1,
      draId: 'DRA-ICICI-99201',
      agentName: 'Suresh Kumar',
      role: 'Field Agent',
      status: 'On Duty (Moving)',
      onLeave: false,
      battery: '78%',
      speed: '28 km/h',
      lastSeen: '2 mins ago',
      lat: 17.4399,
      lng: 78.4983,
      currentLocation: 'Road No. 36, Jubilee Hills, Hyderabad',
      activeTask: 'Visiting ICICI Auto Loan Portfolio'
    },
    {
      id: 2,
      draId: 'DRA-KOTAK-44102',
      agentName: 'Priya Sharma',
      role: 'Telecaller',
      status: 'In Call',
      onLeave: false,
      battery: '100% (AC)',
      speed: 'Stationary',
      lastSeen: 'Just now',
      lat: 17.4435,
      lng: 78.4772,
      currentLocation: 'Apex Agency Central Operations Room',
      activeTask: 'Calling Kotak Credit Card Queue'
    },
    {
      id: 3,
      draId: 'DRA-ICICI-99201',
      agentName: 'Rahul Verma',
      role: 'Field Agent',
      status: 'On Site (At Residence)',
      onLeave: false,
      battery: '64%',
      speed: '0 km/h',
      lastSeen: '6 mins ago',
      lat: 17.3688,
      lng: 78.5316,
      currentLocation: 'Vanasthalipuram Colony, Hyderabad',
      activeTask: 'ICICI Address Verification'
    }
  ]);
  const SEAT_CAP = 4;
  const [newAgentName, setNewAgentName] = useState('');
  const [newAgentRole, setNewAgentRole] = useState('Field Agent');

  const [telecallerNotes, setTelecallerNotes] = useState('');
  const [telecallerDisposition, setTelecallerDisposition] = useState('PTP (Promise to Pay)');
  const [telecallerPtpDate, setTelecallerPtpDate] = useState('2026-09-11T14:00');
  const [telecallerMessage, setTelecallerMessage] = useState('');

  const [fieldDisposition, setFieldDisposition] = useState('PTP (Promise to Pay)');
  const [fieldPtpAmount, setFieldPtpAmount] = useState('50,000');
  const [fieldPtpDateTime, setFieldPtpDateTime] = useState('2026-09-11T14:00');
  const [fieldUtr, setFieldUtr] = useState('');
  const [fieldVisitNotes, setFieldVisitNotes] = useState('');
  const [fieldVisitLogged, setFieldVisitLogged] = useState(false);

  useEffect(() => {
    if (currentUser.isAuthenticated && currentUser.role) {
      localStorage.setItem('debtone_active_role', currentUser.role);
    }
  }, [currentUser]);

  const handleDataIngested = (newParsedCases) => {
    const newBatchId = `BATCH-${Date.now().toString().slice(-6)}`;
    const newBatch = {
      batchId: newBatchId,
      fileName: `Allocation_Dump_${new Date().toISOString().split('T')[0]}.xlsx`,
      bankName: selectedBankFilter === 'ALL' ? 'ICICI Bank' : selectedBankFilter,
      productType: 'RETAIL_LOAN',
      uploadDate: new Date().toLocaleString(),
      totalCases: newParsedCases.length,
      status: 'Active (30-Day Cycle)',
      cases: newParsedCases
    };

    const updatedBatches = [newBatch, ...uploadedBatches];
    setUploadedBatches(updatedBatches);
    try { localStorage.setItem('debtone_uploaded_batches_v2', JSON.stringify(updatedBatches)); } catch (e) {}
    setSelectedBatchId(null);
    setManagerTab('Operations Overview');
  };

  const handleArchiveBatch = (batchId) => {
    const updated = uploadedBatches.map(b => {
      if (b.batchId === batchId) {
        const nextStatus = b.status.includes('Archived') ? 'Active (30-Day Cycle)' : 'Archived (Lifecycle Expired)';
        return { ...b, status: nextStatus };
      }
      return b;
    });
    setUploadedBatches(updated);
    try { localStorage.setItem('debtone_uploaded_batches_v2', JSON.stringify(updated)); } catch (e) {}
  };

  const handleLogin = (e) => {
    e.preventDefault();
    let name = 'Operations User';
    if (loginRole === 'SUPER_ADMIN') name = 'Platform Administrator';
    if (loginRole === 'AGENCY_MANAGER') name = 'Manoj Kumar (Operations Head)';
    if (loginRole === 'TELECALLER') name = 'Priya Sharma (Senior Telecaller)';
    if (loginRole === 'FIELD_AGENT') name = 'Suresh Kumar (Field Executive)';

    setCurrentUser({
      isAuthenticated: true,
      role: loginRole,
      name: name,
      agencyName: 'Apex Recovery Solutions',
      assignedBank: 'Multi-Bank'
    });
  };

  const handleLogout = () => {
    localStorage.removeItem('debtone_active_role');
    setCurrentUser({
      isAuthenticated: false,
      role: 'UNAUTHENTICATED',
      name: '',
      agencyName: '',
      assignedBank: ''
    });
  };

  const handleToggleLeave = (agentId) => {
    setProxyAssignments(proxyAssignments.map(a => {
      if (a.id === agentId) {
        const nextLeave = !a.onLeave;
        alert(`${a.agentName} marked as ${nextLeave ? 'ON LEAVE. Active queues successfully rebalanced to active operators.' : 'ACTIVE ON DUTY.'}`);
        return { ...a, onLeave: nextLeave, status: nextLeave ? 'On Leave (Delegated)' : 'Active' };
      }
      return a;
    }));
  };

  const handleAssignOperator = (e) => {
    e.preventDefault();
    if (!newAgentName) return;

    if (proxyAssignments.length >= SEAT_CAP) {
      alert(`Database Trigger Blocked: Seat capacity reached.`);
      return;
    }

    setProxyAssignments([
      ...proxyAssignments,
      {
        id: Date.now(),
        draId: 'DRA-ICICI-99201',
        agentName: newAgentName,
        role: newAgentRole,
        status: 'Available',
        onLeave: false,
        battery: '90%',
        speed: '0 km/h',
        lastSeen: 'Just enrolled',
        lat: 17.4399,
        lng: 78.4983,
        currentLocation: 'Assigned Hyderabad Cluster',
        activeTask: 'Awaiting case assignment'
      }
    ]);
    setNewAgentName('');
  };

  const handleSaveAllocationRules = (e) => {
    e.preventDefault();
    setRuleSavedMsg('✓ Smart Allocation & Senior Routing Rules saved successfully!');
    setTimeout(() => setRuleSavedMsg(''), 4000);
  };

  const handleTelecallerSubmit = (actionType) => {
    const currentCase = telecallerCases[activeCaseIndex] || telecallerCases[0];
    if (!currentCase) return;

    if (actionType === 'ESCALATE') {
      const updatedBatches = uploadedBatches.map(b => ({
        ...b,
        cases: b.cases.map(c => c.id === currentCase.id ? { ...c, status: 'Escalated to Field', escalatedToField: true, lastFeedback: `[Telecaller Escalation]: ${telecallerNotes || 'Escalated for physical visit'}` } : c)
      }));
      setUploadedBatches(updatedBatches);
      try { localStorage.setItem('debtone_uploaded_batches_v2', JSON.stringify(updatedBatches)); } catch (e) {}
      setTelecallerMessage(`Case ${currentCase.id} successfully escalated to Field Force queue!`);
    } else {
      const updatedBatches = uploadedBatches.map(b => ({
        ...b,
        cases: b.cases.map(c => c.id === currentCase.id ? { ...c, lastFeedback: `[PTP Logged]: Due by ${telecallerPtpDate} - ${telecallerNotes || telecallerDisposition}`, ptpDate: telecallerPtpDate.split('T')[0], ptpAmount: 35000, channel: 'Telecaller', assignedAgent: 'Priya Sharma (Senior Telecaller)' } : c)
      }));
      setUploadedBatches(updatedBatches);
      try { localStorage.setItem('debtone_uploaded_batches_v2', JSON.stringify(updatedBatches)); } catch (e) {}
      setTelecallerMessage(`✓ PTP Stamped (Due: ${telecallerPtpDate}). Advancing to next case...`);
      setTimeout(() => {
        setActiveCaseIndex(prev => (prev + 1) % telecallerCases.length);
        setTelecallerMessage('');
      }, 1000);
    }
  };

  const handleFieldSubmit = (e) => {
    e.preventDefault();
    const currentCase = dailyRouteBatch[activeCaseIndex] || dailyRouteBatch[0];
    if (!currentCase) return;

    const feedbackText = fieldDisposition === 'Paid via UPI on Spot' 
      ? `[Spot Collection]: Paid ₹${fieldPtpAmount} via UPI. UTR: ${fieldUtr}. Verified by GPS.`
      : fieldDisposition === 'PTP (Promise to Pay)'
      ? `[Field PTP]: Promised ₹${fieldPtpAmount} by ${fieldPtpDateTime}. Notes: ${fieldVisitNotes}`
      : `[Field Visit]: ${fieldDisposition} - ${fieldVisitNotes}`;

    const updatedBatches = uploadedBatches.map(b => ({
      ...b,
      cases: b.cases.map(c => c.id === currentCase.id ? { ...c, status: fieldDisposition === 'Paid via UPI on Spot' ? 'Settled / Closed' : 'Field Visited', lastFeedback: feedbackText, ptpDate: fieldPtpDateTime.split('T')[0], ptpAmount: parseFloat(fieldPtpAmount.replace(/,/g, '')) || 50000, channel: 'Field Agent', assignedAgent: 'Suresh Kumar (Field Executive)' } : c)
    }));
    setUploadedBatches(updatedBatches);
    try { localStorage.setItem('debtone_uploaded_batches_v2', JSON.stringify(updatedBatches)); } catch (e) {}
    setFieldVisitLogged(true);
  };

  const handleExportAudit = () => {
    alert(`Bank Audit Package Generated: 100% RBI Audit Ready with all batch reconciliations.`);
  };

  // Filter bank master dataset
  const bankFilteredMaster = allMasterCases.filter(c => {
    if (selectedBankFilter !== 'ALL' && c.bankName !== selectedBankFilter) return false;
    return true;
  });

  // If a specific batch file is clicked, restrict cases to that batch
  const batchFilteredCases = selectedBatchId 
    ? (uploadedBatches.find(b => b.batchId === selectedBatchId)?.cases || [])
    : bankFilteredMaster;

  const filteredCases = batchFilteredCases.filter(c => {
    if (selectedStageFilter === 'TELECALLING' && c.escalatedToField) return false;
    if (selectedStageFilter === 'FIELD' && !c.escalatedToField) return false;
    return true;
  });

  const totalPages = Math.ceil(filteredCases.length / rowsPerPage) || 1;
  const indexOfLastRow = currentPage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;
  const currentTableRows = filteredCases.slice(indexOfFirstRow, indexOfLastRow);

  const telecallerCases = allMasterCases.filter(c => !c.escalatedToField);
  const currentTelecase = telecallerCases[activeCaseIndex] || telecallerCases[0] || {};

  const fieldCases = allMasterCases.filter(c => c.escalatedToField);
  const dailyRouteBatch = fieldCases.slice(0, 15);
  const currentFieldcase = dailyRouteBatch[activeCaseIndex] || dailyRouteBatch[0] || {};

  const totalPosCrores = (filteredCases.reduce((acc, c) => acc + (c.rawAmount || 0), 0) / 10000000).toFixed(2);

  // VIEW: LOGIN GATE
  if (!currentUser.isAuthenticated) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: '#0f172a', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'system-ui, sans-serif', padding: '20px' }}>
        <div style={{ backgroundColor: '#fff', borderRadius: '12px', padding: '40px', maxWidth: '420px', width: '100%', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.3)' }}>
          <div style={{ textAlign: 'center', marginBottom: '28px' }}>
            <h1 style={{ margin: '0 0 6px 0', fontSize: '26px', fontWeight: '800', color: '#0f172a' }}>DebtOne</h1>
            <p style={{ margin: 0, color: '#64748b', fontSize: '14px' }}>Enterprise Debt Recovery & Collection ERP</p>
          </div>

          <form onSubmit={handleLogin} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <div>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#334155', marginBottom: '6px' }}>Select User Role</label>
              <select
                value={loginRole}
                onChange={(e) => setLoginRole(e.target.value)}
                style={{ width: '100%', padding: '10px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '14px', backgroundColor: '#f8fafc' }}
              >
                <option value="AGENCY_MANAGER">Agency Operations Manager</option>
                <option value="TELECALLER">Inbound / Outbound Telecaller</option>
                <option value="FIELD_AGENT">Field Recovery Agent</option>
                <option value="SUPER_ADMIN">Platform Administrator (Super Admin)</option>
              </select>
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#334155', marginBottom: '6px' }}>Email / Username</label>
              <input
                type="text"
                value={loginEmail}
                onChange={(e) => setLoginEmail(e.target.value)}
                placeholder="manager@apexrecovery.in"
                style={{ width: '100%', padding: '10px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '14px', boxSizing: 'border-box' }}
              />
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#334155', marginBottom: '6px' }}>Password</label>
              <input
                type="password"
                defaultValue="password123"
                style={{ width: '100%', padding: '10px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '14px', boxSizing: 'border-box' }}
              />
            </div>

            <button
              type="submit"
              style={{ background: '#0284c7', color: '#fff', padding: '12px', borderRadius: '6px', border: 'none', fontWeight: '600', fontSize: '15px', cursor: 'pointer', marginTop: '8px' }}
            >
              Sign In to Workspace
            </button>
          </form>
        </div>
      </div>
    );
  }

  // SHARED HEADER
  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f8fafc', fontFamily: 'system-ui, -apple-system, sans-serif' }}>
      <header style={{ backgroundColor: '#0f172a', color: '#fff', padding: '0 24px', height: '64px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
          <span style={{ fontSize: '20px', fontWeight: 'bold', letterSpacing: '-0.5px' }}>DebtOne</span>
          {currentUser.role !== 'SUPER_ADMIN' && (
            <span style={{ fontSize: '13px', background: '#1e293b', color: '#38bdf8', padding: '4px 10px', borderRadius: '6px', border: '1px solid #334155', fontWeight: '600' }}>
              🏢 {currentUser.agencyName} ({selectedBankFilter === 'ALL' ? 'Multi-Bank Portfolios' : selectedBankFilter})
            </span>
          )}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: '13px', fontWeight: '600' }}>{currentUser.name}</div>
            <div style={{ fontSize: '11px', color: '#94a3b8' }}>Role: <strong style={{ color: '#38bdf8' }}>{currentUser.role}</strong></div>
          </div>
          <button
            onClick={handleLogout}
            style={{ background: '#334155', border: 'none', color: '#fff', padding: '6px 12px', borderRadius: '4px', fontSize: '12px', cursor: 'pointer', fontWeight: '500' }}
          >
            Switch Role / Logout
          </button>
        </div>
      </header>

      {/* ROLE 1: SUPER ADMIN */}
      {currentUser.role === 'SUPER_ADMIN' && (
        <main style={{ maxWidth: '1200px', margin: '32px auto', padding: '0 24px' }}>
          <div style={{ marginBottom: '24px', padding: '16px', background: '#fef3c7', borderRadius: '8px', border: '1px solid #fde68a', color: '#92400e', fontSize: '14px' }}>
            🔒 <strong>Super Administrator Deck:</strong> Provision agency tenant workspaces and enforce seat tiers.
          </div>
          <AgencyOnboardingPortal />
        </main>
      )}

      {/* ROLE 2: AGENCY MANAGER */}
      {currentUser.role === 'AGENCY_MANAGER' && (
        <div>
          <div style={{ backgroundColor: '#1e293b', padding: '0 24px', display: 'flex', gap: '24px' }}>
            {['Operations Overview', 'Bank Portfolio Upload', 'DRA & Staff Roster', 'Bank Audit Export'].map((tab) => (
              <button
                key={tab}
                onClick={() => setManagerTab(tab)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: managerTab === tab ? '#38bdf8' : '#94a3b8',
                  padding: '14px 0',
                  fontSize: '14px',
                  fontWeight: managerTab === tab ? '600' : '400',
                  cursor: 'pointer',
                  borderBottom: managerTab === tab ? '2px solid #38bdf8' : '2px solid transparent'
                }}
              >
                {tab}
              </button>
            ))}
          </div>

          <main style={{ maxWidth: '1200px', margin: '32px auto', padding: '0 24px' }}>
            {managerTab === 'Operations Overview' && (
              <div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px', background: '#fff', padding: '14px 20px', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                    <span style={{ fontSize: '13px', fontWeight: '700', color: '#0f172a' }}>🏦 Bank Portfolios & Batches:</span>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      {[
                        { key: 'ALL', label: 'All Banks (Master)' },
                        { key: 'ICICI Bank', label: '🏦 ICICI Bank' },
                        { key: 'Kotak Mahindra Bank', label: '🏦 Kotak Mahindra Bank' }
                      ].map(bank => (
                        <button
                          key={bank.key}
                          onClick={() => { 
                            setSelectedBankFilter(bank.key); 
                            setSelectedBatchId(null); 
                            setCurrentPage(1); 
                          }}
                          style={{
                            background: selectedBankFilter === bank.key ? '#0284c7' : '#f8fafc',
                            color: selectedBankFilter === bank.key ? '#fff' : '#334155',
                            border: selectedBankFilter === bank.key ? '1px solid #0284c7' : '1px solid #cbd5e1',
                            padding: '6px 14px',
                            borderRadius: '6px',
                            fontSize: '13px',
                            fontWeight: '600',
                            cursor: 'pointer'
                          }}
                        >
                          {bank.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Batch Upload Shortcut */}
                  <button
                    onClick={() => setManagerTab('Bank Portfolio Upload')}
                    style={{ background: '#047857', color: '#fff', border: 'none', padding: '7px 14px', borderRadius: '6px', fontSize: '12px', fontWeight: '700', cursor: 'pointer' }}
                  >
                    + Upload New Bank Dump (.xlsx)
                  </button>
                </div>

                {/* BANK BATCH DRILL-DOWN SELECTOR BAR */}
                <div style={{ background: '#f8fafc', padding: '16px 20px', borderRadius: '8px', border: '1px solid #cbd5e1', marginBottom: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <div style={{ fontSize: '12px', fontWeight: '700', color: '#334155' }}>📁 Uploaded Batch Files ({selectedBankFilter === 'ALL' ? 'All Banks' : selectedBankFilter})</div>
                    <div style={{ fontSize: '11px', color: '#64748b', marginTop: '2px' }}>Click any batch file to inspect its exact set of cases, lifecycle status, or archive expired batches.</div>
                  </div>
                  
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                    <button
                      onClick={() => setSelectedBatchId(null)}
                      style={{
                        background: selectedBatchId === null ? '#0f172a' : '#fff',
                        color: selectedBatchId === null ? '#fff' : '#334155',
                        border: '1px solid #cbd5e1',
                        padding: '5px 12px',
                        borderRadius: '6px',
                        fontSize: '12px',
                        fontWeight: '600',
                        cursor: 'pointer'
                      }}
                    >
                      All Batches Combined
                    </button>

                    {uploadedBatches
                      .filter(b => selectedBankFilter === 'ALL' || b.bankName === selectedBankFilter)
                      .map(b => (
                        <div key={b.batchId} style={{ display: 'flex', alignItems: 'center', background: selectedBatchId === b.batchId ? '#e0f2fe' : '#fff', border: selectedBatchId === b.batchId ? '1px solid #0284c7' : '1px solid #cbd5e1', borderRadius: '6px', padding: '4px 10px', gap: '8px' }}>
                          <button
                            onClick={() => setSelectedBatchId(b.batchId)}
                            style={{ background: 'none', border: 'none', color: '#0369a1', fontSize: '12px', fontWeight: '700', cursor: 'pointer', padding: 0 }}
                          >
                            📄 {b.fileName} ({b.totalCases} cases)
                          </button>
                          <button
                            onClick={() => handleArchiveBatch(b.batchId)}
                            title="Toggle Archive / Active 30-day Lifecycle"
                            style={{ background: b.status.includes('Archived') ? '#fee2e2' : '#f1f5f9', border: 'none', fontSize: '10px', padding: '2px 6px', borderRadius: '4px', cursor: 'pointer', fontWeight: '600', color: b.status.includes('Archived') ? '#991b1b' : '#334155' }}
                          >
                            {b.status.includes('Archived') ? 'Unarchive' : 'Archive'}
                          </button>
                        </div>
                      ))}
                  </div>
                </div>

                {/* UNIFORM STAT CARDS */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '16px', marginBottom: '24px' }}>
                  <div 
                    onClick={() => { setSelectedStageFilter('ALL'); setCurrentPage(1); }}
                    style={{ background: '#fff', padding: '20px', borderRadius: '8px', border: selectedStageFilter === 'ALL' ? '2px solid #0284c7' : '1px solid #e2e8f0', cursor: 'pointer', boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }}
                  >
                    <div style={{ fontSize: '12px', color: '#64748b' }}>Filtered Cases</div>
                    <div style={{ fontSize: '22px', fontWeight: '700', color: '#0f172a', marginTop: '6px' }}>{filteredCases.length}</div>
                    <div style={{ fontSize: '12px', color: '#059669', marginTop: '4px' }}>
                      {parseFloat(totalPosCrores) > 0 ? `₹${totalPosCrores} Cr Exposure` : 'Active Dataset'}
                    </div>
                  </div>

                  <div 
                    onClick={() => { setSelectedStageFilter('TELECALLING'); setCurrentPage(1); }}
                    style={{ background: '#fff', padding: '20px', borderRadius: '8px', border: selectedStageFilter === 'TELECALLING' ? '2px solid #0284c7' : '1px solid #e2e8f0', cursor: 'pointer', boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }}
                  >
                    <div style={{ fontSize: '12px', color: '#64748b' }}>Telecalling Queue</div>
                    <div style={{ fontSize: '22px', fontWeight: '700', color: '#0284c7', marginTop: '6px' }}>{batchFilteredCases.filter(c => !c.escalatedToField).length}</div>
                    <div style={{ fontSize: '12px', color: '#0284c7', marginTop: '4px', textDecoration: 'underline' }}>Click to view queue →</div>
                  </div>

                  <div 
                    onClick={() => { setSelectedStageFilter('FIELD'); setCurrentPage(1); }}
                    style={{ background: '#fff', padding: '20px', borderRadius: '8px', border: selectedStageFilter === 'FIELD' ? '2px solid #0284c7' : '1px solid #e2e8f0', cursor: 'pointer', boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }}
                  >
                    <div style={{ fontSize: '12px', color: '#64748b' }}>Escalated to Field</div>
                    <div style={{ fontSize: '22px', fontWeight: '700', color: '#d97706', marginTop: '6px' }}>{batchFilteredCases.filter(c => c.escalatedToField).length}</div>
                    <div style={{ fontSize: '12px', color: '#d97706', marginTop: '4px', textDecoration: 'underline' }}>Click to view visits →</div>
                  </div>

                  <div 
                    onClick={() => setManagerTab('PTP Command Center')}
                    style={{ background: '#fff', padding: '20px', borderRadius: '8px', border: '1px solid #e2e8f0', cursor: 'pointer', boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }}
                  >
                    <div style={{ fontSize: '12px', color: '#0284c7', fontWeight: '700' }}>📊 PTP COMMAND CENTER</div>
                    <div style={{ fontSize: '22px', fontWeight: '800', color: '#0284c7', marginTop: '6px' }}>₹85,000</div>
                    <div style={{ fontSize: '12px', color: '#047857', marginTop: '4px', textDecoration: 'underline' }}>Open Morning Briefing →</div>
                  </div>
                  
                  <div 
                    onClick={() => setManagerTab('DRA & Staff Roster')}
                    style={{ background: '#fff', padding: '20px', borderRadius: '8px', border: '1px solid #e2e8f0', cursor: 'pointer', boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div style={{ fontSize: '12px', color: '#166534', fontWeight: '600' }}>TEAM & GPS</div>
                      <span style={{ fontSize: '11px', background: '#dcfce7', color: '#15803d', padding: '2px 6px', borderRadius: '10px', fontWeight: '700' }}>Live 📡</span>
                    </div>
                    <div style={{ fontSize: '22px', fontWeight: '800', color: '#14532d', marginTop: '6px' }}>
                      {proxyAssignments.filter(a => !a.onLeave).length} / {SEAT_CAP} Active
                    </div>
                    <div style={{ fontSize: '12px', color: '#15803d', marginTop: '4px', fontWeight: '600', textDecoration: 'underline' }}>
                      Manage leave →
                    </div>
                  </div>
                </div>

                <div style={{ backgroundColor: '#fff', borderRadius: '8px', padding: '24px', border: '1px solid #e2e8f0' }}>
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '14px' }}>
                      <thead>
                        <tr style={{ borderBottom: '2px solid #e2e8f0', color: '#64748b' }}>
                          <th style={{ padding: '10px' }}>Bank</th>
                          <th style={{ padding: '10px' }}>Account / Card</th>
                          <th style={{ padding: '10px' }}>Customer Name</th>
                          <th style={{ padding: '10px' }}>Outstanding Due</th>
                          <th style={{ padding: '10px' }}>Stage</th>
                          <th style={{ padding: '10px' }}>Details / Feedback</th>
                        </tr>
                      </thead>
                      <tbody>
                        {currentTableRows.map((c, i) => {
                          const absoluteIndex = indexOfFirstRow + i;
                          return (
                            <tr key={c.id || absoluteIndex} style={{ borderBottom: '1px solid #f1f5f9', cursor: 'pointer' }} onClick={() => setActiveCaseIndex(absoluteIndex)}>
                              <td style={{ padding: '12px' }}>
                                <span style={{ fontSize: '11px', background: '#f1f5f9', color: '#334155', padding: '3px 8px', borderRadius: '4px', fontWeight: '600' }}>
                                  {c.bankName || 'ICICI Bank'}
                                </span>
                              </td>
                              <td style={{ padding: '12px', fontWeight: '600' }}>
                                <div>{c.id}</div>
                                {c.collateral && <div style={{ fontSize: '11px', color: '#0284c7', marginTop: '2px' }}>{c.collateral}</div>}
                              </td>
                              <td style={{ padding: '12px' }}>{c.customerName}</td>
                              <td style={{ padding: '12px', color: '#047857', fontWeight: '600' }}>{c.amount}</td>
                              <td style={{ padding: '12px' }}>
                                <span style={{
                                  background: c.escalatedToField ? '#fef3c7' : '#e0f2fe',
                                  color: c.escalatedToField ? '#b45309' : '#0369a1',
                                  padding: '4px 10px',
                                  borderRadius: '4px',
                                  fontSize: '12px',
                                  fontWeight: '600'
                                }}>
                                  {c.status}
                                </span>
                              </td>
                              <td style={{ padding: '12px', color: '#64748b', fontSize: '13px' }}>{c.lastFeedback}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {managerTab === 'Bank Portfolio Upload' && (
              <BatchUploadConsole onDataIngested={handleDataIngested} />
            )}

            {/* PTP COMMAND CENTER PAGE VIEW */}
            {managerTab === 'PTP Command Center' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                <div style={{ backgroundColor: '#fff', borderRadius: '8px', padding: '24px', border: '1px solid #e2e8f0' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', borderBottom: '1px solid #e2e8f0', paddingBottom: '14px' }}>
                    <div>
                      <button
                        onClick={() => setManagerTab('Operations Overview')}
                        style={{ background: '#f1f5f9', border: '1px solid #cbd5e1', color: '#334155', padding: '5px 10px', borderRadius: '4px', fontSize: '12px', fontWeight: '600', cursor: 'pointer', marginBottom: '8px' }}
                      >
                        ← Back to Operations Overview
                      </button>
                      <h3 style={{ margin: '0 0 4px 0', fontSize: '20px', fontWeight: '800', color: '#0f172a' }}>📊 Date-Wise PTP Command Center</h3>
                      <p style={{ color: '#64748b', fontSize: '13px', margin: 0 }}>Review scheduled promises to pay classified by date and collection channel (Telecaller vs. Field Agent).</p>
                    </div>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      {['2026-09-11', '2026-09-12'].map(dateStr => {
                        const dateTotal = allMasterCases
                          .filter(c => c.ptpDate === dateStr)
                          .reduce((sum, c) => sum + (c.ptpAmount || 0), 0);
                        return (
                          <button
                            key={dateStr}
                            onClick={() => setSelectedPtpDateFilter(dateStr)}
                            style={{
                              background: selectedPtpDateFilter === dateStr ? '#0284c7' : '#f8fafc',
                              color: selectedPtpDateFilter === dateStr ? '#fff' : '#334155',
                              border: selectedPtpDateFilter === dateStr ? '1px solid #0284c7' : '1px solid #cbd5e1',
                              padding: '8px 14px',
                              borderRadius: '6px',
                              fontSize: '13px',
                              fontWeight: '700',
                              cursor: 'pointer'
                            }}
                          >
                            📅 {dateStr === '2026-09-11' ? 'Today (Sep 11)' : 'Tomorrow (Sep 12)'} (₹{dateTotal.toLocaleString('en-IN')})
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Channel Breakdown Summary Cards */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '24px' }}>
                    <div style={{ background: '#f0f9ff', border: '1px solid #bae6fd', padding: '16px', borderRadius: '8px' }}>
                      <div style={{ fontSize: '12px', fontWeight: '700', color: '#0369a1' }}>📞 Telecaller Promised Total ({selectedPtpDateFilter})</div>
                      <div style={{ fontSize: '22px', fontWeight: '800', color: '#0369a1', marginTop: '4px' }}>
                        ₹{allMasterCases.filter(c => c.ptpDate === selectedPtpDateFilter && c.channel === 'Telecaller').reduce((s, c) => s + (c.ptpAmount || 0), 0).toLocaleString('en-IN')}
                      </div>
                      <div style={{ fontSize: '12px', color: '#0284c7', marginTop: '2px' }}>
                        {allMasterCases.filter(c => c.ptpDate === selectedPtpDateFilter && c.channel === 'Telecaller').length} Promising Accounts
                      </div>
                    </div>

                    <div style={{ background: '#fef3c7', border: '1px solid #fde68a', padding: '16px', borderRadius: '8px' }}>
                      <div style={{ fontSize: '12px', fontWeight: '700', color: '#b45309' }}>🛵 Field Agent Promised Total ({selectedPtpDateFilter})</div>
                      <div style={{ fontSize: '22px', fontWeight: '800', color: '#b45309', marginTop: '4px' }}>
                        ₹{allMasterCases.filter(c => c.ptpDate === selectedPtpDateFilter && c.channel === 'Field Agent').reduce((s, c) => s + (c.ptpAmount || 0), 0).toLocaleString('en-IN')}
                      </div>
                      <div style={{ fontSize: '12px', color: '#d97706', marginTop: '2px' }}>
                        {allMasterCases.filter(c => c.ptpDate === selectedPtpDateFilter && c.channel === 'Field Agent').length} Field Itinerary Stops
                      </div>
                    </div>
                  </div>

                  {/* Granular Debtor List for Selected Date */}
                  <h4 style={{ margin: '0 0 12px 0', fontSize: '16px', fontWeight: '700', color: '#0f172a' }}>
                    Promised Debtors List for {selectedPtpDateFilter} (Focus & Execution Queue)
                  </h4>

                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '13px' }}>
                      <thead>
                        <tr style={{ borderBottom: '2px solid #e2e8f0', color: '#64748b' }}>
                          <th style={{ padding: '10px' }}>Channel</th>
                          <th style={{ padding: '10px' }}>Account ID & Bank</th>
                          <th style={{ padding: '10px' }}>Customer Name & Phone</th>
                          <th style={{ padding: '10px' }}>Assigned Operator</th>
                          <th style={{ padding: '10px' }}>Promised Amount</th>
                          <th style={{ padding: '10px' }}>Latest Feedback / Notes</th>
                        </tr>
                      </thead>
                      <tbody>
                        {allMasterCases.filter(c => c.ptpDate === selectedPtpDateFilter).length === 0 ? (
                          <tr>
                            <td colSpan="6" style={{ textAlign: 'center', padding: '24px', color: '#64748b' }}>No PTPs recorded for this date.</td>
                          </tr>
                        ) : (
                          allMasterCases.filter(c => c.ptpDate === selectedPtpDateFilter).map((c) => (
                            <tr key={c.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                              <td style={{ padding: '12px' }}>
                                <span style={{ fontSize: '11px', background: c.channel === 'Field Agent' ? '#fef3c7' : '#e0f2fe', color: c.channel === 'Field Agent' ? '#b45309' : '#0369a1', padding: '3px 8px', borderRadius: '4px', fontWeight: '700' }}>
                                  {c.channel || 'Telecaller'}
                                </span>
                              </td>
                              <td style={{ padding: '12px', fontWeight: '600' }}>
                                <div>{c.id}</div>
                                <div style={{ fontSize: '11px', color: '#64748b', fontWeight: '400' }}>{c.bankName}</div>
                              </td>
                              <td style={{ padding: '12px' }}>
                                <div style={{ fontWeight: '600', color: '#0f172a' }}>{c.customerName}</div>
                                <div style={{ fontSize: '11px', color: '#0284c7' }}>📞 {c.phone}</div>
                              </td>
                              <td style={{ padding: '12px', fontWeight: '600', color: '#334155' }}>
                                {c.assignedAgent || 'Priya Sharma'}
                              </td>
                              <td style={{ padding: '12px', color: '#047857', fontWeight: '700', fontSize: '15px' }}>
                                ₹{(c.ptpAmount || 0).toLocaleString('en-IN')}
                              </td>
                              <td style={{ padding: '12px', color: '#64748b', fontSize: '12px' }}>
                                {c.lastFeedback}
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {managerTab === 'DRA & Staff Roster' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
                  <div style={{ backgroundColor: '#fff', borderRadius: '8px', padding: '24px', border: '1px solid #e2e8f0' }}>
                    <h3 style={{ margin: '0 0 6px 0', fontSize: '18px', fontWeight: '700', color: '#0f172a' }}>Enroll Agency Staff</h3>
                    <p style={{ color: '#64748b', fontSize: '13px', margin: '0 0 20px 0' }}>Map telecallers and field staff under your agency's DRA license.</p>

                    <form onSubmit={handleAssignOperator} style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                      <div>
                        <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#334155', marginBottom: '6px' }}>Certified DRA License</label>
                        <select style={{ width: '100%', padding: '9px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '14px' }}>
                          {draHolders.map(h => (
                            <option key={h.id} value={h.id}>{h.id} - {h.name}</option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#334155', marginBottom: '6px' }}>Staff Name</label>
                        <input
                          type="text"
                          value={newAgentName}
                          onChange={(e) => setNewAgentName(e.target.value)}
                          placeholder="e.g. Ramesh Reddy"
                          style={{ width: '100%', padding: '9px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '14px', boxSizing: 'border-box' }}
                        />
                      </div>

                      <div>
                        <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#334155', marginBottom: '6px' }}>Staff Operational Role</label>
                        <select
                          value={newAgentRole}
                          onChange={(e) => setNewAgentRole(e.target.value)}
                          style={{ width: '100%', padding: '9px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '14px' }}
                        >
                          <option value="Field Agent">Field Agent (Bike / Physical Visits)</option>
                          <option value="Telecaller">Telecaller (Indoor Office Calling)</option>
                        </select>
                      </div>

                      <button type="submit" style={{ background: '#0284c7', color: '#fff', padding: '11px', borderRadius: '6px', border: 'none', fontWeight: '600', cursor: 'pointer', marginTop: '6px' }}>
                        Enroll Staff Under DRA License
                      </button>
                    </form>
                  </div>

                  <div style={{ backgroundColor: '#fff', borderRadius: '8px', padding: '24px', border: '1px solid #e2e8f0' }}>
                    <h3 style={{ margin: '0 0 6px 0', fontSize: '18px', fontWeight: '700', color: '#0f172a' }}>Active Staff, Leave Rebalancing & GPS</h3>
                    <p style={{ color: '#64748b', fontSize: '13px', margin: '0 0 16px 0' }}>Manage leave status or inspect real-time agent GPS telemetry.</p>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                      {proxyAssignments.map(a => (
                        <div key={a.id} style={{ border: trackingAgent?.id === a.id ? '2px solid #0284c7' : '1px solid #e2e8f0', padding: '12px', borderRadius: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: trackingAgent?.id === a.id ? '#f0f9ff' : '#fff' }}>
                          <div>
                            <div style={{ fontWeight: '600', fontSize: '14px', color: '#0f172a' }}>{a.agentName} ({a.role})</div>
                            <div style={{ fontSize: '11px', color: a.onLeave ? '#dc2626' : '#059669', marginTop: '2px', fontWeight: '600' }}>
                              ● {a.onLeave ? 'ON LEAVE (Queue Delegated)' : 'ACTIVE ON DUTY'}
                            </div>
                          </div>
                          
                          <div style={{ display: 'flex', gap: '6px' }}>
                            <button
                              onClick={() => setTrackingAgent(a)}
                              style={{ background: '#0f172a', color: '#38bdf8', border: 'none', padding: '6px 10px', borderRadius: '6px', fontSize: '11px', fontWeight: '700', cursor: 'pointer' }}
                            >
                              🛰️ Track GPS
                            </button>
                            <button
                              onClick={() => handleToggleLeave(a.id)}
                              style={{ background: a.onLeave ? '#10b981' : '#ef4444', color: '#fff', border: 'none', padding: '6px 10px', borderRadius: '6px', fontSize: '11px', fontWeight: '700', cursor: 'pointer' }}
                            >
                              {a.onLeave ? 'Set Active' : 'Leave'}
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* GPS Telemetry Modal Panel */}
                {trackingAgent && (
                  <div style={{ backgroundColor: '#0f172a', color: '#fff', borderRadius: '8px', padding: '24px', border: '1px solid #334155', boxShadow: '0 4px 12px rgba(0,0,0,0.2)' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #334155', paddingBottom: '14px', marginBottom: '18px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <span style={{ fontSize: '22px' }}>🛰️</span>
                        <div>
                          <h4 style={{ margin: 0, fontSize: '18px', fontWeight: '700', color: '#38bdf8' }}>
                            Real-Time GPS Inspector: {trackingAgent.agentName} ({trackingAgent.role})
                          </h4>
                          <div style={{ fontSize: '12px', color: '#94a3b8', marginTop: '2px' }}>
                            Hardware GPS Signal: Verified • Telemetry Refresh Rate: 10s • Proxy: {trackingAgent.draId}
                          </div>
                        </div>
                      </div>
                      <button
                        onClick={() => setTrackingAgent(null)}
                        style={{ background: '#334155', border: 'none', color: '#fff', padding: '6px 12px', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}
                      >
                        ✕ Close Map
                      </button>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr', gap: '20px' }}>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px' }}>
                        <div style={{ background: '#1e293b', padding: '14px', borderRadius: '6px', border: '1px solid #334155' }}>
                          <div style={{ fontSize: '11px', color: '#94a3b8' }}>CURRENT LATITUDE / LONGITUDE</div>
                          <div style={{ fontSize: '16px', fontWeight: '700', color: '#fff', marginTop: '4px' }}>{trackingAgent.lat}° N, {trackingAgent.lng}° E</div>
                        </div>
                        <div style={{ background: '#1e293b', padding: '14px', borderRadius: '6px', border: '1px solid #334155' }}>
                          <div style={{ fontSize: '11px', color: '#94a3b8' }}>SPEED & TRANSIT VELOCITY</div>
                          <div style={{ fontSize: '16px', fontWeight: '700', color: '#f59e0b', marginTop: '4px' }}>{trackingAgent.speed}</div>
                        </div>
                        <div style={{ background: '#1e293b', padding: '14px', borderRadius: '6px', border: '1px solid #334155', gridColumn: 'span 2' }}>
                          <div style={{ fontSize: '11px', color: '#94a3b8' }}>GEOLOCATION ADDRESS</div>
                          <div style={{ fontSize: '14px', fontWeight: '600', color: '#fff', marginTop: '4px' }}>📍 {trackingAgent.currentLocation}</div>
                        </div>
                        <div style={{ background: '#1e293b', padding: '14px', borderRadius: '6px', border: '1px solid #334155', gridColumn: 'span 2' }}>
                          <div style={{ fontSize: '11px', color: '#94a3b8' }}>ACTIVE ASSIGNED TASK</div>
                          <div style={{ fontSize: '14px', fontWeight: '600', color: '#34d399', marginTop: '4px' }}>🎯 {trackingAgent.activeTask}</div>
                        </div>
                      </div>

                      <div style={{ background: '#1e293b', padding: '16px', borderRadius: '6px', border: '1px solid #334155', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                        <div>
                          <div style={{ fontSize: '12px', fontWeight: '600', color: '#94a3b8', marginBottom: '8px' }}>SATELLITE POSITION PREVIEW</div>
                          <div style={{ height: '140px', background: '#0f172a', borderRadius: '6px', border: '1px dashed #334155', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                            <div style={{ fontSize: '30px' }}>🛵</div>
                            <div style={{ fontSize: '12px', color: '#38bdf8', fontWeight: '700', marginTop: '4px' }}>Agent: {trackingAgent.agentName}</div>
                          </div>
                        </div>
                        <a
                          href={`https://www.google.com/maps?q=${trackingAgent.lat},${trackingAgent.lng}`}
                          target="_blank"
                          rel="noreferrer"
                          style={{ display: 'block', textAlign: 'center', background: '#047857', color: '#fff', textDecoration: 'none', padding: '11px', borderRadius: '6px', fontSize: '13px', fontWeight: '700', marginTop: '12px' }}
                        >
                          🌐 Open Exact Coordinate in Google Maps ↗
                        </a>
                      </div>
                    </div>
                  </div>
                )}

                {/* MANAGER RULE BUILDER PANEL */}
                <div style={{ backgroundColor: '#fff', borderRadius: '8px', padding: '24px', border: '1px solid #e2e8f0' }}>
                  <h3 style={{ margin: '0 0 6px 0', fontSize: '18px', fontWeight: '700', color: '#0f172a' }}>⚡ Smart Workload Allocation & Senior Routing Rules</h3>
                  <p style={{ color: '#64748b', fontSize: '13px', margin: '0 0 20px 0' }}>Configure automated routing criteria for high-value accounts and slab distribution across telecallers.</p>

                  {ruleSavedMsg && (
                    <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', color: '#15803d', padding: '10px 14px', borderRadius: '6px', fontSize: '13px', marginBottom: '16px', fontWeight: '600' }}>
                      {ruleSavedMsg}
                    </div>
                  )}

                  <form onSubmit={handleSaveAllocationRules} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px', alignItems: 'end' }}>
                    <div>
                      <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#334155', marginBottom: '6px' }}>High-Value Slab Threshold (₹)</label>
                      <input
                        type="text"
                        value={allocationRule.highValueThreshold}
                        onChange={(e) => setAllocationRule({ ...allocationRule, highValueThreshold: e.target.value })}
                        style={{ width: '100%', padding: '9px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '14px', boxSizing: 'border-box' }}
                      />
                    </div>

                    <div>
                      <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#334155', marginBottom: '6px' }}>Distribution Strategy</label>
                      <select
                        value={allocationRule.distributionMode}
                        onChange={(e) => setAllocationRule({ ...allocationRule, distributionMode: e.target.value })}
                        style={{ width: '100%', padding: '9px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '14px' }}
                      >
                        <option value="ROUND_ROBIN">Equitable Round-Robin</option>
                        <option value="SENIOR_WEIGHTED">Senior-Weighted (High-Value Priority)</option>
                      </select>
                    </div>

                    <button
                      type="submit"
                      style={{ background: '#047857', color: '#fff', padding: '11px', borderRadius: '6px', border: 'none', fontWeight: '700', fontSize: '14px', cursor: 'pointer' }}
                    >
                      Save & Apply Rules
                    </button>
                  </form>
                </div>
              </div>
            )}

            {managerTab === 'Bank Audit Export' && (
              <div style={{ backgroundColor: '#fff', borderRadius: '8px', padding: '32px', border: '1px solid #e2e8f0', maxWidth: '600px', margin: '0 auto', textAlign: 'center' }}>
                <h3 style={{ margin: '0 0 8px 0', fontSize: '20px', fontWeight: '700', color: '#0f172a' }}>Bank Audit Defense Exporter</h3>
                <p style={{ color: '#64748b', fontSize: '14px', margin: '0 0 24px 0' }}>Export a sealed, tamper-proof compliance report formatted to ICICI Bank and RBI guidelines.</p>
                <button
                  onClick={handleExportAudit}
                  style={{ background: '#047857', color: '#fff', padding: '14px 28px', borderRadius: '6px', border: 'none', fontWeight: '700', fontSize: '15px', cursor: 'pointer' }}
                >
                  Download Bank Audit Package (PDF / Excel)
                </button>
              </div>
            )}
          </main>
        </div>
      )}

      {/* ROLE 3: TELECALLER WORKBENCH */}
      {currentUser.role === 'TELECALLER' && (
        <main style={{ maxWidth: '950px', margin: '32px auto', padding: '0 24px' }}>
          <div style={{ backgroundColor: '#fff', borderRadius: '12px', padding: '28px', border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', borderBottom: '1px solid #e2e8f0', paddingBottom: '14px' }}>
              <div>
                <span style={{ fontSize: '12px', background: '#e0f2fe', color: '#0369a1', padding: '3px 8px', borderRadius: '4px', fontWeight: '600' }}>
                  Telecaller Assigned Queue ({telecallerCases.length} cases)
                </span>
                <h2 style={{ margin: '6px 0 0 0', fontSize: '20px', fontWeight: '700', color: '#0f172a' }}>Priya Sharma's Active Dialing Session</h2>
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  onClick={() => setTelecallerMode('TABLE')}
                  style={{ background: telecallerMode === 'TABLE' ? '#0f172a' : '#f1f5f9', color: telecallerMode === 'TABLE' ? '#fff' : '#334155', border: 'none', padding: '7px 14px', borderRadius: '6px', fontSize: '13px', fontWeight: '600', cursor: 'pointer' }}
                >
                  📋 Assigned List View
                </button>
                <button
                  onClick={() => setTelecallerMode('SEQUENTIAL')}
                  style={{ background: telecallerMode === 'SEQUENTIAL' ? '#0284c7' : '#f1f5f9', color: telecallerMode === 'SEQUENTIAL' ? '#fff' : '#334155', border: 'none', padding: '7px 14px', borderRadius: '6px', fontSize: '13px', fontWeight: '600', cursor: 'pointer' }}
                >
                  ▶ Sequential Power Dialer
                </button>
              </div>
            </div>

            {telecallerMessage && (
              <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', color: '#15803d', padding: '12px 16px', borderRadius: '6px', fontSize: '14px', fontWeight: '600', marginBottom: '16px' }}>
                {telecallerMessage}
              </div>
            )}

            {telecallerMode === 'TABLE' && (
              <div>
                <p style={{ fontSize: '13px', color: '#64748b', marginBottom: '16px' }}>
                  Below are all soft-calling cases assigned to your queue. Click any case to launch its dialing workspace or switch to Sequential Power Dialer mode above.
                </p>
                <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '13px' }}>
                    <thead>
                      <tr style={{ borderBottom: '2px solid #e2e8f0', color: '#64748b' }}>
                        <th style={{ padding: '10px' }}>Account / Card</th>
                        <th style={{ padding: '10px' }}>Customer Name</th>
                        <th style={{ padding: '10px' }}>Phone</th>
                        <th style={{ padding: '10px' }}>Overdue Due</th>
                        <th style={{ padding: '10px' }}>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {telecallerCases.map((c, idx) => (
                        <tr key={c.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                          <td style={{ padding: '10px', fontWeight: '600' }}>{c.id}</td>
                          <td style={{ padding: '10px' }}>{c.customerName}</td>
                          <td style={{ padding: '10px', color: '#0284c7' }}>{c.phone}</td>
                          <td style={{ padding: '10px', color: '#047857', fontWeight: '600' }}>{c.amount}</td>
                          <td style={{ padding: '10px' }}>
                            <button
                              onClick={() => { setActiveCaseIndex(idx); setTelecallerMode('SEQUENTIAL'); }}
                              style={{ background: '#0284c7', color: '#fff', border: 'none', padding: '5px 10px', borderRadius: '4px', fontSize: '12px', cursor: 'pointer', fontWeight: '600' }}
                            >
                              Dial Case →
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {telecallerMode === 'SEQUENTIAL' && (
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#f8fafc', padding: '14px 18px', borderRadius: '8px', border: '1px solid #e2e8f0', marginBottom: '20px' }}>
                  <div>
                    <div style={{ fontSize: '12px', color: '#64748b' }}>Sequential Queue Progress</div>
                    <div style={{ fontSize: '15px', fontWeight: '700', color: '#0f172a', marginTop: '2px' }}>
                      Debtor {activeCaseIndex + 1} of {telecallerCases.length}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                      onClick={() => setActiveCaseIndex(prev => (prev - 1 + telecallerCases.length) % telecallerCases.length)}
                      style={{ background: '#fff', border: '1px solid #cbd5e1', padding: '6px 12px', borderRadius: '4px', fontSize: '12px', cursor: 'pointer', fontWeight: '600' }}
                    >
                      ← Previous Debtor
                    </button>
                    <button
                      onClick={() => setActiveCaseIndex(prev => (prev + 1) % telecallerCases.length)}
                      style={{ background: '#0284c7', color: '#fff', border: 'none', padding: '6px 14px', borderRadius: '4px', fontSize: '12px', cursor: 'pointer', fontWeight: '600' }}
                    >
                      Next Debtor →
                    </button>
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px', background: '#f8fafc', padding: '16px', borderRadius: '6px', border: '1px solid #e2e8f0', marginBottom: '24px' }}>
                  <div>
                    <div style={{ fontSize: '12px', color: '#64748b' }}>Customer Name</div>
                    <div style={{ fontSize: '15px', fontWeight: '700', color: '#0f172a', marginTop: '2px' }}>{currentTelecase.customerName}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: '12px', color: '#64748b' }}>Primary Contact (Click to Dial)</div>
                    <div style={{ fontSize: '15px', fontWeight: '700', color: '#0284c7', marginTop: '2px' }}>📞 {currentTelecase.phone}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: '12px', color: '#64748b' }}>Outstanding Due</div>
                    <div style={{ fontSize: '15px', fontWeight: '700', color: '#047857', marginTop: '2px' }}>{currentTelecase.amount}</div>
                  </div>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#334155', marginBottom: '6px' }}>Call Outcome Disposition</label>
                    <select
                      value={telecallerDisposition}
                      onChange={(e) => setTelecallerDisposition(e.target.value)}
                      style={{ width: '100%', padding: '10px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '14px' }}
                    >
                      <option value="PTP (Promise to Pay)">PTP (Promise to Pay - Agrees to clear)</option>
                      <option value="NC (Not Contactable / Switch Off)">NC (Not Contactable / Switched Off)</option>
                      <option value="NL (Ringing / Not Lifting)">NL (Ringing / Not Lifting)</option>
                      <option value="RF (Refused to Pay / Dispute)">RF (Customer Refused / Hostile)</option>
                    </select>
                  </div>

                  {telecallerDisposition === 'PTP (Promise to Pay)' && (
                    <div style={{ background: '#f0f9ff', padding: '14px', borderRadius: '8px', border: '1px solid #bae6fd' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                        <label style={{ fontSize: '13px', fontWeight: '700', color: '#0369a1' }}>📅 Promised Payment Date & Time</label>
                        <span style={{ fontSize: '10px', background: '#dcfce7', color: '#15803d', padding: '1px 6px', borderRadius: '8px', fontWeight: '700' }}>🔒 Auto-Locked</span>
                      </div>
                      <input
                        type="datetime-local"
                        value={telecallerPtpDate}
                        onChange={(e) => setTelecallerPtpDate(e.target.value)}
                        style={{ width: '100%', padding: '10px 12px', borderRadius: '6px', border: '1px solid #0284c7', fontSize: '14px', backgroundColor: '#fff', boxSizing: 'border-box' }}
                      />
                    </div>
                  )}

                  <div>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#334155', marginBottom: '6px' }}>Call Feedback Notes</label>
                    <textarea
                      rows={2}
                      value={telecallerNotes}
                      onChange={(e) => setTelecallerNotes(e.target.value)}
                      placeholder="Record customer interaction notes..."
                      style={{ width: '100%', padding: '10px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '14px', boxSizing: 'border-box' }}
                    />
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginTop: '8px' }}>
                    <button
                      type="button"
                      onClick={() => handleTelecallerSubmit('SAVE')}
                      style={{ background: '#0284c7', color: '#fff', padding: '12px', borderRadius: '6px', border: 'none', fontWeight: '600', fontSize: '14px', cursor: 'pointer' }}
                    >
                      💾 Save & Stamp PTP ➔
                    </button>

                    <button
                      type="button"
                      onClick={() => handleTelecallerSubmit('ESCALATE')}
                      style={{ background: '#ea580c', color: '#fff', padding: '12px', borderRadius: '6px', border: 'none', fontWeight: '700', fontSize: '14px', cursor: 'pointer' }}
                    >
                      🚨 Escalate to Field Force
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </main>
      )}

      {/* ROLE 4: FIELD AGENT MOBILE VIEW */}
      {currentUser.role === 'FIELD_AGENT' && (
        <main style={{ maxWidth: '580px', margin: '24px auto', padding: '0 16px' }}>
          <div style={{ backgroundColor: '#fff', borderRadius: '12px', padding: '24px', border: '1px solid #e2e8f0', boxShadow: '0 2px 6px rgba(0,0,0,0.06)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', borderBottom: '1px solid #e2e8f0', paddingBottom: '12px' }}>
              <div>
                <span style={{ fontSize: '12px', background: '#fef3c7', color: '#b45309', padding: '3px 8px', borderRadius: '4px', fontWeight: '700' }}>
                  🛵 Field Route ({dailyRouteBatch.length} Stops)
                </span>
                <h3 style={{ margin: '4px 0 0 0', fontSize: '16px', fontWeight: '700', color: '#0f172a' }}>Suresh Kumar's Itinerary</h3>
              </div>
              <div style={{ display: 'flex', gap: '6px' }}>
                <button
                  onClick={() => setFieldMode('TABLE')}
                  style={{ background: fieldMode === 'TABLE' ? '#0f172a' : '#f1f5f9', color: fieldMode === 'TABLE' ? '#fff' : '#334155', border: 'none', padding: '6px 10px', borderRadius: '6px', fontSize: '12px', fontWeight: '600', cursor: 'pointer' }}
                >
                  📋 List View
                </button>
                <button
                  onClick={() => setFieldMode('GUIDED')}
                  style={{ background: fieldMode === 'GUIDED' ? '#047857' : '#f1f5f9', color: fieldMode === 'GUIDED' ? '#fff' : '#334155', border: 'none', padding: '6px 10px', borderRadius: '6px', fontSize: '12px', fontWeight: '600', cursor: 'pointer' }}
                >
                  📍 Guided Stop
                </button>
              </div>
            </div>

            {fieldMode === 'TABLE' && (
              <div>
                <p style={{ fontSize: '13px', color: '#64748b', marginBottom: '14px' }}>
                  Review your assigned daily route stops. Tap any stop to jump directly to its guided visit execution screen.
                </p>
                <div style={{ maxHeight: '420px', overflowY: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '13px' }}>
                    <thead>
                      <tr style={{ borderBottom: '2px solid #e2e8f0', color: '#64748b' }}>
                        <th style={{ padding: '8px' }}>Stop</th>
                        <th style={{ padding: '8px' }}>Customer & Address</th>
                        <th style={{ padding: '8px' }}>Due</th>
                        <th style={{ padding: '8px' }}>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dailyRouteBatch.map((c, idx) => (
                        <tr key={c.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                          <td style={{ padding: '10px', fontWeight: '700', color: '#0284c7' }}>#{idx + 1}</td>
                          <td style={{ padding: '10px' }}>
                            <div style={{ fontWeight: '600', color: '#0f172a' }}>{c.customerName}</div>
                            <div style={{ fontSize: '11px', color: '#64748b' }}>{c.address}</div>
                          </td>
                          <td style={{ padding: '10px', color: '#047857', fontWeight: '600' }}>{c.amount}</td>
                          <td style={{ padding: '10px' }}>
                            <button
                              onClick={() => { setActiveCaseIndex(idx); setFieldMode('GUIDED'); }}
                              style={{ background: '#047857', color: '#fff', border: 'none', padding: '5px 10px', borderRadius: '4px', fontSize: '12px', cursor: 'pointer', fontWeight: '600' }}
                            >
                              Visit →
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {fieldMode === 'GUIDED' && (
              <div>
                <div style={{ background: '#f8fafc', padding: '12px', borderRadius: '8px', border: '1px solid #e2e8f0', marginBottom: '16px', fontSize: '13px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                    <div style={{ fontWeight: '700', color: '#0f172a' }}>📍 Stop {activeCaseIndex + 1} of {dailyRouteBatch.length}</div>
                    
                    <a
                      href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(currentFieldcase.address || 'Hyderabad')}`}
                      target="_blank"
                      rel="noreferrer"
                      style={{ background: '#0284c7', color: '#fff', textDecoration: 'none', padding: '5px 10px', borderRadius: '4px', fontSize: '12px', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '4px' }}
                    >
                      <span>🗺️ Navigate via Google Maps ↗</span>
                    </a>
                  </div>

                  <div style={{ color: '#0284c7' }}>Target: <strong>{currentFieldcase.customerName}</strong></div>
                  <div style={{ color: '#64748b', marginTop: '2px' }}>Address: {currentFieldcase.address}</div>
                </div>

                <h2 style={{ margin: '0 0 4px 0', fontSize: '18px', fontWeight: '800', color: '#0f172a' }}>{currentFieldcase.customerName}</h2>
                <div style={{ fontSize: '13px', color: '#64748b', marginBottom: '12px' }}>Case: {currentFieldcase.id} | Due: <strong style={{ color: '#047857' }}>{currentFieldcase.amount}</strong></div>

                <form onSubmit={handleFieldSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#334155', marginBottom: '6px' }}>Visit Status</label>
                    <select
                      value={fieldDisposition}
                      onChange={(e) => setFieldDisposition(e.target.value)}
                      style={{ width: '100%', padding: '10px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '14px' }}
                    >
                      <option value="PTP (Promise to Pay)">PTP (Promise to Pay)</option>
                      <option value="Door Locked / Address Untraceable">Door Locked / Address Untraceable</option>
                      <option value="Paid via UPI on Spot">Paid via UPI on Spot</option>
                      <option value="Refused to Cooperate">Refused to Cooperate</option>
                    </select>
                  </div>

                  {fieldDisposition === 'PTP (Promise to Pay)' && (
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                      <div>
                        <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#334155', marginBottom: '6px' }}>Committed (₹)</label>
                        <input
                          type="text"
                          value={fieldPtpAmount}
                          onChange={(e) => setFieldPtpAmount(e.target.value)}
                          style={{ width: '100%', padding: '9px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '14px', boxSizing: 'border-box' }}
                        />
                      </div>
                      <div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                          <label style={{ fontSize: '13px', fontWeight: '600', color: '#334155' }}>Promised Date & Time</label>
                          <span style={{ fontSize: '10px', background: '#dcfce7', color: '#15803d', padding: '1px 6px', borderRadius: '8px', fontWeight: '700' }}>🔒 Auto-Locked</span>
                        </div>
                        <input
                          type="datetime-local"
                          value={fieldPtpDateTime}
                          onChange={(e) => setFieldPtpDateTime(e.target.value)}
                          style={{ width: '100%', padding: '9px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '13px', boxSizing: 'border-box', backgroundColor: '#fff' }}
                        />
                      </div>
                    </div>
                  )}

                  {fieldDisposition === 'Paid via UPI on Spot' && (
                    <div>
                      <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#334155', marginBottom: '6px' }}>Bank UTR / Ref No.</label>
                      <input
                        type="text"
                        value={fieldUtr}
                        onChange={(e) => setFieldUtr(e.target.value)}
                        placeholder="e.g. 4291823719"
                        style={{ width: '100%', padding: '9px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '14px', boxSizing: 'border-box' }}
                      />
                    </div>
                  )}

                  <div style={{ background: '#f0fdf4', padding: '12px', borderRadius: '6px', border: '1px solid #bbf7d0', fontSize: '12px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    <div style={{ color: '#15803d', fontWeight: '600' }}>✓ GPS Geo-tag: 17.4399° N, 78.4983° E (Verified on Location)</div>
                    <div style={{ color: '#15803d', fontWeight: '600' }}>✓ Door Selfie: Captured & Hash Sealed</div>
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#334155', marginBottom: '6px' }}>Field Visit Notes</label>
                    <textarea
                      rows={2}
                      value={fieldVisitNotes}
                      onChange={(e) => setFieldVisitNotes(e.target.value)}
                      placeholder="Record observations, gatekeeper comments..."
                      style={{ width: '100%', padding: '9px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '13px', boxSizing: 'border-box' }}
                    />
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                    <button
                      type="button"
                      onClick={() => setActiveCaseIndex(prev => (prev + 1) % dailyRouteBatch.length)}
                      style={{ background: '#334155', color: '#fff', padding: '11px', borderRadius: '6px', border: 'none', fontWeight: '600', fontSize: '13px', cursor: 'pointer' }}
                    >
                      Skip Stop ➔
                    </button>
                    <button
                      type="submit"
                      style={{ background: '#047857', color: '#fff', padding: '11px', borderRadius: '6px', border: 'none', fontWeight: '700', fontSize: '14px', cursor: 'pointer' }}
                    >
                      Submit Visit Proof
                    </button>
                  </div>

                  {fieldVisitLogged && (
                    <div style={{ textAlign: 'center', color: '#059669', fontSize: '13px', fontWeight: '600', marginTop: '6px' }}>
                      ✓ Visit proof recorded and route advanced to next stop!
                    </div>
                  )}
                </form>
              </div>
            )}
          </div>
        </main>
      )}
    </div>
  );
};

export default ManagerPortalLayout;